/**
 * The picture guide's tasks: one job each, a picture and a few words a step.
 *
 * Written for someone who would rather not read. Each sentence is short, says
 * one thing, and names the button exactly as the till shows it. The pictures
 * carry the rest.
 *
 * Every sentence is a `Words` so other languages can sit beside the English
 * without touching the pictures: add the language to `LANGS` and its text to
 * each step. Until a step has it, the English shows.
 */

import {
  ArrowDown,
  ArrowRight,
  Banknote,
  Ban,
  Check,
  CreditCard,
  CupSoda,
  KeyRound,
  LogIn,
  Monitor,
  Moon,
  Pause,
  Play,
  PlugZap,
  Sandwich,
  ShieldAlert,
  Smartphone,
  Sunrise,
  Undo2,
  UserRound,
  Wheat,
  WifiOff,
  X,
  ZoomIn,
} from "lucide-react";
import type { ComponentType, ReactNode } from "react";

import {
  AppIcon,
  Basket,
  Big,
  Btn,
  Chip,
  Coin,
  Field,
  Keypad,
  Note,
  PinDots,
  Row,
  Screen,
  Tap,
  Tile,
  Verdict,
  type Tone,
} from "./scene";
import { TillMap, type Where } from "./tillmap";

export const LANGS = [{ code: "en", name: "English" }] as const;
export type Lang = (typeof LANGS)[number]["code"];
export type Words = { en: string } & Partial<Record<Lang, string>>;

export function say(words: Words, lang: Lang): string {
  return words[lang] ?? words.en;
}

export interface Step {
  words: Words;
  /** Where on the till this happens: the whole screen, with this part lit. */
  where?: Where;
  /** What to press there, close up. */
  picture: ReactNode;
}

export interface Task {
  id: string;
  title: Words;
  icon: ComponentType<{ className?: string }>;
  tone: Tone;
  steps: Step[];
}

const SALE: [string, number, string][] = [
  ["Coca-Cola 500ml", 2, "R24.00"],
  ["White bread", 1, "R18.00"],
];

const GRID = (
  <Row>
    <Tap>
      <Tile name="Coca-Cola 500ml" price="R12.00" hue={0} icon={CupSoda} />
    </Tap>
    <Tile name="White bread" price="R18.00" hue={40} icon={Sandwich} />
    <Tile name="Maize meal" price="R65.00" hue={200} icon={Wheat} />
  </Row>
);

function LoginTabs() {
  return (
    <span className="inline-flex rounded-control bg-surface p-1 shadow-card">
      <Tap>
        <span className="inline-flex h-10 items-center gap-2 rounded-control bg-brand-500 px-4 font-semibold text-white">
          <KeyRound className="size-4" /> Till
        </span>
      </Tap>
      <span className="inline-flex h-10 items-center px-4 font-medium text-ink-500">
        Back office
      </span>
    </span>
  );
}

function ManagerPin() {
  return (
    <div className="grid w-full max-w-[17rem] gap-2 rounded-card bg-warn-50 p-3">
      <span className="inline-flex items-center gap-2 text-sm font-semibold text-warn-700">
        <UserRound className="size-4" /> Manager
      </span>
      <Field label="Manager's staff code" value="SIPHO" wide />
      <PinDots filled={4} />
    </div>
  );
}

export const TASKS: Task[] = [
  {
    id: "sign-in",
    title: { en: "Sign in" },
    icon: LogIn,
    tone: "brand",
    steps: [
      {
        words: { en: "Tap Till." },
        where: { screen: "login", focus: "tabs" },
        picture: <LoginTabs />,
      },
      {
        words: { en: "Type your staff code." },
        where: { screen: "login", focus: "code" },
        picture: <Field label="Staff code" value="NOMSA" />,
      },
      {
        words: { en: "Type your PIN. Hide it with your hand." },
        where: { screen: "login", focus: "pin" },
        picture: (
          <div className="grid justify-items-center gap-3">
            <PinDots filled={2} />
            <Keypad pressed="7" />
          </div>
        ),
      },
      {
        words: { en: "Tap Open POS." },
        where: { screen: "login", focus: "open" },
        picture: (
          <Tap>
            <Btn variant="primary" size="lg">
              Open POS
            </Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "cash",
    title: { en: "Sell for cash" },
    icon: Banknote,
    tone: "money",
    steps: [
      {
        words: { en: "Tap the product. Or scan it." },
        where: { screen: "till", focus: "grid" },
        picture: GRID,
      },
      {
        words: { en: "Check the sale with the customer." },
        where: { screen: "till", focus: "basket" },
        picture: <Basket lines={SALE} total="R42.00" />,
      },
      {
        words: { en: "Tap Cash." },
        where: { screen: "till", focus: "cashcard" },
        picture: (
          <Row>
            <Tap>
              <Btn icon={<Banknote className="size-4" />} size="pos">
                Cash (F10)
              </Btn>
            </Tap>
            <Btn icon={<CreditCard className="size-4" />} size="pos">
              Card (F11)
            </Btn>
          </Row>
        ),
      },
      {
        words: { en: "Type the money the customer gave you." },
        where: { screen: "payment", focus: "received" },
        picture: (
          <div className="grid justify-items-center gap-3">
            <Field label="Cash received" value="R50.00" />
            <Row>
              <Note rand={20} small />
              <Tap>
                <Note rand={50} small />
              </Tap>
              <Note rand={100} small />
            </Row>
          </div>
        ),
      },
      {
        words: { en: "Tap Complete." },
        where: { screen: "payment", focus: "complete" },
        picture: (
          <Tap>
            <Btn variant="money" size="pos">
              Complete · R42.00
            </Btn>
          </Tap>
        ),
      },
      {
        words: { en: "Give the change. The till shows how much." },
        where: { screen: "receipt", focus: "change" },
        picture: (
          <div className="grid justify-items-center gap-3">
            <span className="text-3xl font-extrabold text-money-700">Change R8.00</span>
            <Row>
              <Coin rand="R5" />
              <Coin rand="R2" />
              <Coin rand="R1" />
            </Row>
          </div>
        ),
      },
    ],
  },
  {
    id: "card",
    title: { en: "Sell by card" },
    icon: CreditCard,
    tone: "navy",
    steps: [
      {
        words: { en: "Tap the product. Or scan it." },
        where: { screen: "till", focus: "grid" },
        picture: GRID,
      },
      {
        words: { en: "Tap Card." },
        where: { screen: "till", focus: "cashcard" },
        picture: (
          <Row>
            <Btn icon={<Banknote className="size-4" />} size="pos">
              Cash (F10)
            </Btn>
            <Tap>
              <Btn icon={<CreditCard className="size-4" />} size="pos">
                Card (F11)
              </Btn>
            </Tap>
          </Row>
        ),
      },
      {
        words: { en: "The customer pays on the card machine. Wait for approved." },
        picture: (
          <Verdict ok>
            <Big tone="navy">
              <Smartphone />
            </Big>
          </Verdict>
        ),
      },
      {
        words: { en: "Tap Complete." },
        where: { screen: "payment", focus: "complete" },
        picture: (
          <Tap>
            <Btn variant="money" size="pos">
              Complete · R42.00
            </Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "hold",
    title: { en: "Wait for a customer" },
    icon: Pause,
    tone: "gold",
    steps: [
      {
        words: { en: "Customer must fetch money? Tap Hold." },
        where: { screen: "till", focus: "hold" },
        picture: (
          <Tap>
            <Btn variant="ghost" icon={<Pause className="size-4" />}>
              Hold
            </Btn>
          </Tap>
        ),
      },
      {
        words: { en: "Help the next customer." },
        where: { screen: "till", focus: "grid" },
        picture: GRID,
      },
      {
        words: { en: "Customer is back? Tap Held." },
        where: { screen: "till", focus: "held" },
        picture: (
          <Tap>
            <Btn variant="ghost" icon={<Play className="size-4" />}>
              Held (1)
            </Btn>
          </Tap>
        ),
      },
      {
        words: { en: "Tap Resume. Then take the money." },
        where: { screen: "held", focus: "resume" },
        picture: (
          <div className="flex w-full max-w-[17rem] items-center justify-between rounded-card border border-line bg-surface p-3">
            <span className="text-left text-sm">
              <span className="block font-semibold text-ink-900">10:05 · 3 items</span>
              <span className="font-numeric">R42.00</span>
            </span>
            <Tap>
              <Btn size="sm" variant="primary">
                Resume
              </Btn>
            </Tap>
          </div>
        ),
      },
    ],
  },
  {
    id: "refund",
    title: { en: "Give money back" },
    icon: Undo2,
    tone: "warn",
    steps: [
      {
        words: { en: "Find the sale in Recent sales. Tap the receipt number." },
        where: { screen: "till", focus: "recent" },
        picture: (
          <div className="grid w-full max-w-[17rem] gap-1.5 rounded-card border border-line bg-surface p-3 text-left text-sm">
            <span className="font-semibold text-ink-900">Recent sales</span>
            <Tap>
              <span className="flex w-full justify-between gap-6 font-numeric text-brand-500">
                <span>T01-0042</span>
                <span className="text-ink-900">R42.00</span>
              </span>
            </Tap>
            <span className="flex justify-between font-numeric text-ink-500">
              <span>T01-0041</span>
              <span>R18.00</span>
            </span>
          </div>
        ),
      },
      {
        words: { en: "Tap Refund." },
        where: { screen: "receipt", focus: "refund" },
        picture: (
          <Tap>
            <Btn icon={<Undo2 className="size-4" />}>Refund</Btn>
          </Tap>
        ),
      },
      {
        words: { en: "Choose what comes back. Use + and −." },
        where: { screen: "refund", focus: "lines" },
        picture: (
          <div className="flex w-full max-w-[17rem] items-center justify-between rounded-card border border-line bg-surface p-3 text-sm">
            <span className="font-medium text-ink-900">Coca-Cola 500ml</span>
            <span className="flex items-center gap-2 font-numeric text-lg">
              <span className="grid size-8 place-items-center rounded-control border border-line">
                −
              </span>
              1
              <Tap>
                <span className="grid size-8 place-items-center rounded-control border border-line">
                  +
                </span>
              </Tap>
            </span>
          </div>
        ),
      },
      {
        words: { en: "Good to sell again? Back on the shelf. Broken or bad? Written off." },
        where: { screen: "refund", focus: "shelf" },
        picture: (
          <Row>
            <span className="inline-flex items-center gap-2 rounded-control bg-money-50 px-3 py-2 text-sm font-semibold text-money-700">
              <Check className="size-5" strokeWidth={3} /> Back on the shelf
            </span>
            <span className="inline-flex items-center gap-2 rounded-control bg-danger-50 px-3 py-2 text-sm font-semibold text-danger-700">
              <X className="size-5" strokeWidth={3} /> Written off
            </span>
          </Row>
        ),
      },
      {
        words: { en: "Type why it came back." },
        where: { screen: "refund", focus: "reason" },
        picture: <Field label="Why is it coming back?" value="It was flat" wide />,
      },
      {
        words: { en: "Asked for a manager? The manager types their own code and PIN." },
        where: { screen: "refund", focus: "manager" },
        picture: <ManagerPin />,
      },
      {
        words: { en: "Tap Refund. Give the money back." },
        where: { screen: "refund", focus: "button" },
        picture: (
          <Row>
            <Tap>
              <Btn variant="primary" size="pos">
                Refund R12.00
              </Btn>
            </Tap>
            <Coin rand="R5" />
            <Note rand={10} small />
          </Row>
        ),
      },
    ],
  },
  {
    id: "no-signal",
    title: { en: "No signal" },
    icon: WifiOff,
    tone: "warn",
    steps: [
      {
        words: { en: "Orange at the top means no signal." },
        where: { screen: "till", focus: "chip", offline: true },
        picture: <Chip online={false} waiting={3} />,
      },
      {
        words: { en: "Keep selling. Every sale is saved on the till." },
        where: { screen: "till", focus: "basket", offline: true },
        picture: (
          <Verdict ok>
            <Basket lines={SALE} total="R42.00" />
          </Verdict>
        ),
      },
      {
        words: { en: "Never ring the sales up again." },
        picture: (
          <Verdict ok={false}>
            <Basket lines={SALE} total="R42.00" />
          </Verdict>
        ),
      },
      {
        words: { en: "Green again? Everything is sent. Nothing to do." },
        where: { screen: "till", focus: "chip" },
        picture: <Chip online />,
      },
    ],
  },
  {
    id: "power",
    title: { en: "Power cut" },
    icon: PlugZap,
    tone: "navy",
    steps: [
      {
        words: { en: "Power back? Tap the SmartSpaza icon." },
        where: { screen: "home", focus: "icon" },
        picture: (
          <Tap>
            <AppIcon />
          </Tap>
        ),
      },
      {
        words: { en: "Sign in with your PIN. No signal needed." },
        where: { screen: "login", focus: "pin" },
        picture: (
          <div className="grid justify-items-center gap-3">
            <PinDots filled={4} />
            <Keypad />
          </div>
        ),
      },
      {
        words: { en: "The sale is still there. Check it with the customer." },
        where: { screen: "till", focus: "basket" },
        picture: (
          <Verdict ok>
            <Basket lines={SALE} total="R42.00" />
          </Verdict>
        ),
      },
    ],
  },
  {
    id: "start-day",
    title: { en: "Start the day" },
    icon: Sunrise,
    tone: "gold",
    steps: [
      {
        words: { en: "Tap Open shift." },
        where: { screen: "till", focus: "shiftbar" },
        picture: (
          <Tap>
            <Btn variant="primary">Open shift</Btn>
          </Tap>
        ),
      },
      {
        words: { en: "Count the money in the drawer." },
        picture: (
          <Row>
            <Note rand={200} />
            <Note rand={100} />
            <Note rand={100} />
            <Note rand={50} />
            <Note rand={50} />
          </Row>
        ),
      },
      {
        words: { en: "Type the amount. Tap Open shift." },
        where: { screen: "open-shift", focus: "float" },
        picture: (
          <div className="grid justify-items-center gap-4">
            <Field label="Opening float (R)" value="500.00" />
            <Tap>
              <Btn variant="primary">Open shift</Btn>
            </Tap>
          </div>
        ),
      },
    ],
  },
  {
    id: "end-day",
    title: { en: "End the day" },
    icon: Moon,
    tone: "navy",
    steps: [
      {
        words: { en: "Tap Cash up." },
        where: { screen: "till", focus: "shiftbar" },
        picture: (
          <Tap>
            <Btn variant="primary" size="sm">
              Cash up
            </Btn>
          </Tap>
        ),
      },
      {
        words: { en: "Count every note and coin. Type how many." },
        where: { screen: "cash-up", focus: "counts" },
        picture: (
          <div className="grid w-full max-w-[17rem] gap-2">
            {(
              [
                [200, 3],
                [100, 5],
                [50, 4],
              ] as const
            ).map(([rand, count]) => (
              <span key={rand} className="flex items-center justify-between gap-3">
                <Note rand={rand} small />
                <span className="text-ink-500">×</span>
                <span className="grid h-9 w-14 place-items-center rounded-control border-2 border-brand-500 bg-surface font-numeric font-semibold text-ink-900">
                  {count}
                </span>
              </span>
            ))}
          </div>
        ),
      },
      {
        words: { en: "Tap Close shift." },
        where: { screen: "cash-up", focus: "close" },
        picture: (
          <Tap>
            <Btn variant="primary" size="lg">
              Close shift
            </Btn>
          </Tap>
        ),
      },
      {
        words: { en: "Short? Tell the manager why. It is not a punishment." },
        where: { screen: "shift-closed", focus: "result" },
        picture: (
          <span className="rounded-card bg-warn-50 px-5 py-3 text-xl font-bold text-warn-700">
            Short by R20.00
          </span>
        ),
      },
    ],
  },
  {
    id: "never",
    title: { en: "Never do this" },
    icon: ShieldAlert,
    tone: "danger",
    steps: [
      {
        words: { en: "Never tell anyone your PIN. Not even SmartSpaza." },
        picture: (
          <Verdict ok={false}>
            <span className="grid justify-items-center gap-2 rounded-card bg-surface p-4 shadow-card">
              <PinDots filled={4} />
            </span>
          </Verdict>
        ),
      },
      {
        words: { en: "Never ring up a sale twice." },
        picture: (
          <Verdict ok={false}>
            <Basket lines={SALE} total="R42.00" />
          </Verdict>
        ),
      },
      {
        words: { en: "Never clear the browser or the app's data." },
        picture: (
          <Verdict ok={false}>
            <Big tone="danger">
              <Ban />
            </Big>
          </Verdict>
        ),
      },
      {
        words: { en: "Forgot your PIN? Ask the manager." },
        picture: (
          <Big tone="brand">
            <UserRound />
          </Big>
        ),
      },
    ],
  },
];

/**
 * A step's pictures: where on the screen, then what to press there.
 *
 * Side by side on a wide screen, one above the other on a phone, with an arrow
 * from the first to the second. A step that happens away from the screen —
 * counting the drawer, the card machine — has only the close-up.
 */
export function Picture({ step }: { step: Step }) {
  if (!step.where) return <Screen big>{step.picture}</Screen>;
  return (
    <div className="grid items-center gap-2 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:gap-3 print:grid-cols-1">
      <figure className="grid gap-1.5">
        <figcaption className="flex items-center justify-center gap-1.5 text-sm font-semibold text-ink-500">
          <Monitor className="size-4" aria-hidden />
          On the screen
        </figcaption>
        <TillMap where={step.where} />
      </figure>
      <span className="grid place-items-center text-gold-600 print:hidden" aria-hidden>
        <ArrowDown className="size-7 md:hidden" strokeWidth={2.5} />
        <ArrowRight className="hidden size-7 md:block" strokeWidth={2.5} />
      </span>
      <figure className="grid gap-1.5">
        <figcaption className="flex items-center justify-center gap-1.5 text-sm font-semibold text-ink-500">
          <ZoomIn className="size-4" aria-hidden />
          Close up
        </figcaption>
        <Screen>{step.picture}</Screen>
      </figure>
    </div>
  );
}
